import { observer } from 'mobx-react'
import { useEffect } from 'react'
import type { RouteProps } from 'react-router'
import { matchPath, Redirect, Route, useLocation } from 'react-router'

import commonRepo from '@/repository/common.repo'
import { toastFailure } from '@/utils/toast'

import { getEntrancePage, RouteName } from './RouteName'
import { ROUTE_RESTRICTION_MESSAGE_MAP } from './routeRestrictionMessage'

type Props = RouteProps & {
  redirectTo?: string
}

/**
 * featurePermissions 기반 route 접근 제어 컴포넌트
 *
 * TEACHER_FEATURE_CONFIG의 gatedRoutes에 등록된 경로는
 * 해당 feature가 DENY일 때 redirectTo로 리다이렉트한다.
 * 매핑되지 않은 경로는 항상 통과한다.
 */
const PermissionGuardRoute = observer(
  ({
    redirectTo = getEntrancePage({
      isSchoolflat: commonRepo.isSchoolflat,
      isSeoltab: commonRepo.isSeoltab,
    }),
    ...routeProps
  }: Props) => {
    const { pathname } = useLocation()

    const paths: string[] = Array.isArray(routeProps.path)
      ? routeProps.path
      : routeProps.path !== undefined
        ? [routeProps.path]
        : []

    const matchedPath = paths.find((p) =>
      matchPath(pathname, {
        path: p,
        exact: routeProps.exact,
        strict: routeProps.strict,
        sensitive: routeProps.sensitive,
      }),
    )

    const isBlocked =
      matchedPath !== undefined && !commonRepo.permission.isRouteAllowed(matchedPath)
    // 학원그룹 학습지는 학원이 그룹에 속해 있을 때만 안내 토스트를 띄운다.
    // groupId가 없으면 탭 자체가 노출되지 않으므로 안내 토스트도 띄우지 않고 redirect만 한다.
    const isAcademyGroupRoute = matchedPath === RouteName.academyGroupShareScopeWorksheet
    const restrictionMessage =
      matchedPath && !(isAcademyGroupRoute && !commonRepo.academy?.groupId)
        ? ROUTE_RESTRICTION_MESSAGE_MAP[
            matchedPath as keyof typeof ROUTE_RESTRICTION_MESSAGE_MAP
          ]?.()
        : undefined

    useEffect(() => {
      if (isBlocked && restrictionMessage) {
        toastFailure(restrictionMessage)
      }
    }, [isBlocked, restrictionMessage])

    if (isBlocked) {
      return <Redirect to={redirectTo} />
    }

    return <Route {...routeProps} />
  },
)

export default PermissionGuardRoute
